// ── usePolymarketUsRelayFills — twin of usePolymarketRelayFills ────────
//
// The trader's persisted Polymarket US fill history (/api/polymarket-us/fills,
// written by the relay's order-ledger ingest). Identity-scoped with an
// epoch object per identity visit; a failed read retains the last validated
// rows (#457) and polling continues so a fix recovers without a manual
// refresh. A coded refusal — including the lane-off 503 — reads as
// 'unavailable', never as an error banner.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listPolyUsFillsViaRelay,
  type PolymarketUsFill,
  type PolyUsReadCredentialContext,
  type PolyUsRelayUnavailableCode,
} from '@/lib/polymarketUs/relay';
import { useAuthStore } from '@/stores/authStore';

export type PolyUsRelayFillsStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface PolyUsRelayFillsState {
  fills: PolymarketUsFill[];
  status: PolyUsRelayFillsStatus;
  error: string | null;
  unavailableCode: PolyUsRelayUnavailableCode | null;
  credentialContext: PolyUsReadCredentialContext;
  lastFetchMs: number | null;
}

export interface UsePolymarketUsRelayFillsOptions {
  enabled?: boolean;
  pollMs?: number;
  limit?: number;
}

export interface UsePolymarketUsRelayFillsReturn extends PolyUsRelayFillsState {
  refresh: () => Promise<void>;
}

interface Stamped extends PolyUsRelayFillsState {
  identity: string;
}
interface Scope {
  identity: string;
}

const IDLE: PolyUsRelayFillsState = {
  fills: [],
  status: 'idle',
  error: null,
  unavailableCode: null,
  credentialContext: null,
  lastFetchMs: null,
};
const DEFAULT_POLL_MS = 10_000;

export function usePolymarketUsRelayFills(options: UsePolymarketUsRelayFillsOptions = {}): UsePolymarketUsRelayFillsReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const limit = options.limit;
  const identity = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);
  const [stamped, setStamped] = useState<Stamped>(() => ({ ...IDLE, identity }));
  const current: PolyUsRelayFillsState = stamped.identity === identity ? stamped : IDLE;
  const stateRef = useRef<Stamped>(stamped);
  const scopeRef = useRef<Scope | null>(null);
  const inFlightRef = useRef<Scope | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (scopeRef.current?.identity !== identity) scopeRef.current = { identity };
    const scope = scopeRef.current;
    if (inFlightRef.current === scope) return;
    inFlightRef.current = scope;
    const carry = (): PolyUsRelayFillsState => (stateRef.current.identity === identity ? stateRef.current : IDLE);
    const apply = (next: PolyUsRelayFillsState) => {
      if (!mountedRef.current || scopeRef.current !== scope) return;
      const resolved: Stamped = { ...next, identity };
      stateRef.current = resolved;
      setStamped(resolved);
    };
    if (carry().status === 'idle') apply({ ...carry(), status: 'loading' });
    try {
      const out = await listPolyUsFillsViaRelay(limit);
      const lastFetchMs = Date.now();
      if (out.ok) {
        apply({
          fills: out.fills,
          status: 'ready',
          error: null,
          unavailableCode: null,
          credentialContext: out.credentialContext === undefined ? 'CONTEXT_CHECK_FAILED' : out.credentialContext,
          lastFetchMs,
        });
      } else if (out.code) {
        apply({
          ...carry(),
          status: 'unavailable',
          error: out.error,
          unavailableCode: out.code,
          credentialContext: out.code === 'POLYMARKET_US_NOT_CONFIGURED' ? 'CONTEXT_CHECK_FAILED' : out.code,
          lastFetchMs,
        });
      } else {
        apply({ ...carry(), status: 'error', error: out.error, unavailableCode: null, lastFetchMs });
      }
    } finally {
      if (inFlightRef.current === scope) inFlightRef.current = null;
    }
  }, [limit, identity]);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    void refresh();
    const id = window.setInterval(() => {
      if (!stopped) void refresh();
    }, pollMs);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [enabled, pollMs, refresh]);

  return { ...current, refresh };
}
